import React from 'react';
import PropTypes from 'prop-types';

import filter from 'lodash/filter';
import moment from 'moment';

import Matchup from '~/components/Matchup/Matchup';
import TeamStats from '~/components/TeamStats/TeamStats';
import GameDetailsCard from '~/components/GameDetailsCard/GameDetailsCard';
import KeyPlayersCard from '~/components/KeyPlayersCard/KeyPlayersCard';
import LineScoreCard from '~/components/LineScoreCard/LineScoreCard';
import TeamComparisonCard from '~/components/TeamComparisonCard/TeamComparisonCard';
import TeamStatsGetTheApp from '~/components/TeamStatsGetTheApp/TeamStatsGetTheApp';
import MatchupSection from '~/components/MatchupSection/MatchupSection';
import Ad, { AdTypes } from '~/containers/AdContainer/AdContainer';

import normalise from '~/schemas/teamStats/normalise';
import { NBA, NBA_PREGAME } from '~/schemas/teamStats/nbaSchema';
import { WNBA, WNBA_PREGAME } from '~/schemas/teamStats/wnbaSchema';

import formatKeyPlayers from '~/util/formatKeyPlayers';
import getOr from '~/util/getOr';
import StyledComponent from '~/components/Styled/Styled';

import { injectIntl, intlShape } from 'react-intl';
import css from '~/components/NBAMatchup/NBAMatchup.scss';

const adjustEventDetails = event => {
  let eventDetails = event.event_details;
  const preGame = event.event_status === 'pre_game';
  if (preGame) {
    eventDetails = filter(
      eventDetails,
      detail =>
        !detail.identifier.includes('broadcast') && !detail.identifier.includes('previous_matchup')
    );
    eventDetails.push({
      label: 'Start Time',
      identifier: 'start_time',
      content: moment(event.game_date).format('h:mm A MMM D'),
    });
  }
  return eventDetails;
};

const renderLineScoreCard = event => (
  <MatchupSection>
    <LineScoreCard event={event} />
  </MatchupSection>
);

const renderKeyPlayersCard = (keyPlayers, title) => (
  <MatchupSection>
    <KeyPlayersCard stats={keyPlayers.stats} players={keyPlayers.players} title={title} />
  </MatchupSection>
);

const renderGameDetailsCard = event => (
  <MatchupSection>
    <GameDetailsCard eventDetails={adjustEventDetails(event)} />
  </MatchupSection>
);

const renderTeamComparisonCard = event => (
  <MatchupSection>
    <TeamComparisonCard event={event} />
  </MatchupSection>
);

const renderLastGame = lastGame => (
  <MatchupSection>
    <div className={css.headerBox}>
      <div className={css.headerBoxText}>Last Game</div>
    </div>
    {lastGame ? (
      <div className={css.lastGameText}>{lastGame}</div>
    ) : (
      <div className={css.lastGameText}>None</div>
    )}
  </MatchupSection>
);

const renderTeamComparisonAndLastGame = (event, lastGame) => (
  <MatchupSection>
    {renderTeamComparisonCard(event)}
    <MatchupSection>{renderLastGame(lastGame)}</MatchupSection>
  </MatchupSection>
);

const renderMatchupCard = event => (
  <MatchupSection>
    <Matchup event={event} />
  </MatchupSection>
);

class NBAMatchup extends React.PureComponent {
  renderTeamStats(event, statsToDisplay) {
    const homeTeamName = getOr(event, 'home_team.abbreviation', '');
    const awayTeamName = getOr(event, 'away_team.abbreviation', '');

    return (
      <MatchupSection>
        <div className={css.teamStatsCard}>
          <h2 className={css.cardHeader}>Team Stats</h2>
          <div className={css.teamStats}>
            <TeamStats
              homeTeamName={homeTeamName}
              awayTeamName={awayTeamName}
              stats={statsToDisplay}
            />
          </div>
          {this.props.intl.locale === 'en' && (
            <div className={`${css.teamStatsGetTheApp} ${css.mobileView}`}>
              <TeamStatsGetTheApp leagueSlug={'nba'} />
            </div>
          )}
        </div>
      </MatchupSection>
    );
  }

  render() {
    const event = this.props.event;
    const lastGame = event.last_matchup;
    const keyPlayers = formatKeyPlayers(event);
    const preGame = event.event_status === 'pre_game';
    const inProgress = event.event_status === 'in_progress';
    const keyPlayersTitle = preGame ? 'Key Players' : 'Top Performers';
    const preGameStats = event.league.slug === 'wnba' ? WNBA_PREGAME : NBA_PREGAME;
    const postGameStats = event.league.slug === 'wnba' ? WNBA : NBA;
    const statsToDisplay = preGame
      ? normalise(this.props.stats.team, preGameStats)
      : normalise(this.props.stats.team, postGameStats);
    const leagueSlug = getOr(this.props.event, 'league.slug', '');

    return (
      <div>
        <div className="row">
          <div className={`col-sm-8 ${css.column}`}>
            {renderMatchupCard(event)}
            <div className={css.mobileView}>
              {!preGame && renderLineScoreCard(event)}
              {!inProgress &&
                keyPlayers.players &&
                renderKeyPlayersCard(keyPlayers, keyPlayersTitle)}
            </div>
            {this.renderTeamStats(event, statsToDisplay)}
            <div className={css.mobileView}>
              {preGame && renderTeamComparisonAndLastGame(event, lastGame)}
            </div>
            <div className={css.mobileView}>{renderGameDetailsCard(event)}</div>
          </div>
          <div className={`${css.desktopView} col-sm-4 ${css.column}`}>
            {renderGameDetailsCard(event)}
            <Ad leagueSlug={leagueSlug} adType={AdTypes.BIGBOX_DESKTOP} />
            {!preGame && renderLineScoreCard(event)}
            {!inProgress && keyPlayers.players && renderKeyPlayersCard(keyPlayers, keyPlayersTitle)}
            {preGame && renderTeamComparisonAndLastGame(event, lastGame)}
          </div>
        </div>
      </div>
    );
  }
}

NBAMatchup.propTypes = {
  event: PropTypes.object.isRequired,
  intl: intlShape.isRequired,
  stats: PropTypes.object.isRequired,
};

NBAMatchup.displayName = 'NBAMatchup';

export default injectIntl(StyledComponent(NBAMatchup, [css]));
